import { useCallback, useState } from "react";
import { Upload } from "lucide-react";
import { cn } from "@/lib/utils";

export interface FileDropzoneProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
}

/**
 * Drag-and-drop file input — Plan II restyle (matches Job - files.png).
 *
 * Visual: rounded card with thick dashed navy border (border-border). On
 * drag-over the border switches to primary and the surface tints navy
 * (#f5f8fc) to signal the active drop target. Icon is the lucide `Upload`
 * glyph centred above the prompt text.
 *
 * Props/behaviour are unchanged from the pre-Plan-II implementation — this
 * is a visual-only refresh so existing consumers don't need to update.
 */
export function FileDropzone({ onFiles, accept, multiple = true, disabled }: FileDropzoneProps) {
  const [active, setActive] = useState(false);

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLLabelElement>) => {
      e.preventDefault();
      setActive(false);
      if (disabled) return;
      const files = Array.from(e.dataTransfer.files);
      if (files.length) onFiles(multiple ? files : files.slice(0, 1));
    },
    [disabled, multiple, onFiles],
  );

  return (
    <label
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setActive(true); }}
      // Only clear active when leaving the dropzone entirely — crossing into a
      // child element (icon, span, input) would otherwise toggle active off and
      // flicker the highlight.
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setActive(false);
      }}
      onDrop={onDrop}
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 transition-colors",
        active ? "border-primary bg-[#e5effa]" : "border-primary/50 bg-[#f5f8fc] hover:border-primary",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      <Upload className="size-8 text-muted-foreground" aria-hidden />
      <span className="text-sm font-medium">Drop files here or click to browse</span>
      <input
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        className="hidden"
        onChange={(e) => onFiles(Array.from(e.target.files ?? []))}
      />
    </label>
  );
}
